import { useField, useFormikContext } from 'formik'
import React, { ChangeEventHandler, CSSProperties } from 'react'

import FieldError from '@/components/form/FieldError'
import FieldLabel, {
  ModalHint,
  TooltipHint,
} from '@/components/form/FieldLabel'
import { FieldDescription, FieldGroup } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group'
import { cn } from '@/lib/utils'

export interface TextInputProps {
  autoComplete?: string | undefined
  autoFocus?: boolean | AutoFocusFn
  className?: string
  inputGroupAddon?: React.ReactNode
  label?: string | React.ReactNode
  max?: number
  min?: number
  name: string
  onChange?: (newValue: string) => void
  placeholder?: string
  style?: CSSProperties
  tooltipHint?: TooltipHint
  modalHint?: ModalHint
  type?: string
  isDisabled?: boolean
  transformErrorMessage?: (originalError: string) => React.ReactElement | string
  step?: string
  helpText?: React.ReactNode | string
  labelClassName?: string
  maxLength?: number
}

type AutoFocusFn = (value: string) => boolean

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      autoComplete,
      autoFocus,
      className,
      inputGroupAddon,
      label,
      max,
      min,
      name,
      onChange,
      placeholder,
      style,
      tooltipHint,
      modalHint,
      type = 'text',
      isDisabled,
      transformErrorMessage,
      step,
      helpText,
      labelClassName,
      maxLength,
    },
    ref,
  ) => {
    const { submitCount } = useFormikContext()
    const [field, { error }] = useField(name)
    const id = `field_${name}`
    const showError = !!(submitCount > 0 && error)
    const value = field.value

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      const value = event.target.value

      if (maxLength && value.length > maxLength) {
        // Don't allow typing more than maxLength characters
        return
      }

      field.onChange(event)

      if (onChange) {
        onChange(value)
      }
    }

    const renderWithInputGroup = () => (
      <InputGroup className={cn({ 'bg-disabled opacity-50': isDisabled })}>
        {inputGroupAddon && (
          <InputGroupAddon>{inputGroupAddon}</InputGroupAddon>
        )}
        {renderFormControl()}
      </InputGroup>
    )

    const renderWithoutInputGroup = () => {
      return renderFormControl()
    }

    const renderFormControl = () => {
      const InputComponent = inputGroupAddon ? InputGroupInput : Input

      return (
        <InputComponent
          ref={ref}
          data-field={id}
          id={id}
          aria-invalid={showError || undefined}
          {...field}
          type={type}
          onChange={handleChange}
          autoComplete={autoComplete}
          autoFocus={
            typeof autoFocus === 'function' ? autoFocus(field.value) : autoFocus
          }
          placeholder={placeholder}
          className={className}
          style={style}
          min={min}
          max={max}
          disabled={isDisabled}
          step={step}
        />
      )
    }

    const shouldShowLabelOrMaxLength = label || maxLength

    return (
      <FieldGroup>
        {shouldShowLabelOrMaxLength && (
          <div className="flex justify-between gap-2">
            {label ? (
              <FieldLabel
                htmlFor={id}
                tooltipHint={tooltipHint}
                modalHint={modalHint}
                className={labelClassName}
              >
                {label}
              </FieldLabel>
            ) : (
              <div />
            )}

            {maxLength && (
              <div className="text-muted-foreground text-xxs">
                {value.length}/{maxLength}
              </div>
            )}
          </div>
        )}

        {inputGroupAddon ? renderWithInputGroup() : renderWithoutInputGroup()}

        {helpText && <FieldDescription>{helpText}</FieldDescription>}

        {showError && (
          <FieldError inputId={id}>
            {transformErrorMessage ? transformErrorMessage(error) : error}
          </FieldError>
        )}
      </FieldGroup>
    )
  },
)

export default TextInput
